'use client';

import { cn, Avatar, AvatarImage, AvatarFallback } from './_adapter';
import { Heart, MessageCircle, Repeat2, Share, Bookmark } from 'lucide-react';

interface XPostProps {
  authorName: string;
  authorHandle?: string;
  authorAvatarUrl?: string;
  verified?: boolean;
  content: string;
  media?: React.ReactNode;
  className?: string;
}

export function XPost({
  authorName,
  authorHandle,
  authorAvatarUrl,
  verified = false,
  content,
  media,
  className,
}: XPostProps) {
  const initials = authorName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handle = authorHandle || authorName.toLowerCase().replace(/\s+/g, '');

  return (
    <div className={cn('bg-white dark:bg-zinc-900 rounded-xl border p-4 max-w-lg', className)}>
      {/* Header */}
      <div className="flex items-start gap-3">
        <Avatar className="h-10 w-10">
          {authorAvatarUrl ? (
            <AvatarImage src={authorAvatarUrl} alt={authorName} />
          ) : null}
          <AvatarFallback className="bg-zinc-800 text-white text-sm font-medium">
            {initials}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          {/* Author info */}
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-bold text-sm text-foreground">{authorName}</span>
            {verified && <VerifiedBadge />}
            <span className="text-muted-foreground text-sm">@{handle}</span>
            <span className="text-muted-foreground text-sm">·</span>
            <span className="text-muted-foreground text-sm">now</span>
          </div>

          {/* Content */}
          <div className="mt-1">
            <p className="text-sm text-foreground whitespace-pre-wrap">{content}</p>
          </div>

          {/* Media */}
          {media && <div className="mt-3 rounded-2xl overflow-hidden">{media}</div>}

          {/* Actions */}
          <div className="mt-3 flex items-center justify-between max-w-md">
            <ActionButton icon={MessageCircle} count={0} />
            <ActionButton icon={Repeat2} count={0} />
            <ActionButton icon={Heart} count={0} />
            <ActionButton icon={Share} />
            <ActionButton icon={Bookmark} />
          </div>
        </div>

        {/* X Logo */}
        <XLogo className="h-5 w-5 text-foreground flex-shrink-0" />
      </div>
    </div>
  );
}

function ActionButton({
  icon: Icon,
  count,
}: {
  icon: React.ComponentType<{ className?: string }>;
  count?: number;
}) {
  return (
    <button className="flex items-center gap-1 text-muted-foreground hover:text-blue-500 transition-colors group">
      <div className="p-2 rounded-full group-hover:bg-blue-500/10 transition-colors">
        <Icon className="w-4 h-4" />
      </div>
      {count !== undefined && <span className="text-xs">{count}</span>}
    </button>
  );
}

function VerifiedBadge() {
  return (
    <svg
      viewBox="0 0 22 22"
      className="w-4 h-4 text-blue-500"
      fill="currentColor"
      aria-label="Verified account"
    >
      <path d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.854-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.688-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.634.433 1.218.877 1.688.47.443 1.054.747 1.687.878.633.132 1.29.084 1.897-.136.274.586.705 1.084 1.246 1.439.54.354 1.17.551 1.816.569.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.239 1.266.296 1.903.164.636-.132 1.22-.447 1.68-.907.46-.46.776-1.044.908-1.681s.075-1.299-.165-1.903c.586-.274 1.084-.705 1.439-1.246.354-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z" />
    </svg>
  );
}

function XLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-label="X">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
